import * as React from "react";
import { useQueryClient } from "@tanstack/react-query";

import useMutate from "../../../../../../hooks/useMutate";
import { useLabels } from "../../../../../../reactQuery/boards";
import { createAndUpdateLabel } from "../../../../../../services/apiCards";

export function useLabelEditorState({ labels, boardId, cardId, setShowLabel }) {
  const [inputValue, setInputValue] = React.useState("");
  const [selectedLabels, setSelectedLabels] = React.useState(
    labels.map((item) => item.label)
  );
  const [prevLabels, setPrevLabels] = React.useState([...selectedLabels]);
  const [isError, setIsError] = React.useState(false);
  const queryClient = useQueryClient();
  const { labels: labelsQuery } = useLabels(boardId);

  const options =
    labelsQuery?.data?.data?.responseData.map((item) => item.label) || [];

  const { mutate: createAndUpdateLabelMutate } = useMutate(
    createAndUpdateLabel,
    ["mineCards", "watching"],
    () => {},
    () => {
      queryClient.invalidateQueries({
        queryKey: ["card", Number(cardId)],
      });
      queryClient.invalidateQueries({
        queryKey: ["card", cardId],
      });
      queryClient.invalidateQueries({
        queryKey: ["cards", boardId],
      });
      queryClient.invalidateQueries({
        queryKey: ["labels", boardId],
      });
      queryClient.invalidateQueries({
        queryKey: ["cards", boardId.toString()],
      });
      queryClient.invalidateQueries({
        queryKey: ["history", cardId],
      });
      queryClient.invalidateQueries({
        queryKey: ["history", Number(cardId)],
      });
      queryClient.invalidateQueries({
        queryKey: ["mineCards"],
      });
      queryClient.invalidateQueries({
        queryKey: ["userCards"],
      });
      queryClient.invalidateQueries({
        queryKey: ["assignedCards"],
      });
      queryClient.invalidateQueries({
        queryKey: ["watching"],
      });
      setShowLabel(false);
    },
    () => {}
  );

  React.useEffect(() => {
    const nextLabels = labels.map((item) => item.label);
    setSelectedLabels(nextLabels);
    setPrevLabels(nextLabels);
  }, [labels]);

  const submitLabels = React.useCallback(() => {
    const trimmedInput = inputValue.trim();
    const nextLabels = trimmedInput
      ? [...new Set([...selectedLabels, trimmedInput])]
      : [...new Set(selectedLabels)];

    if (JSON.stringify(prevLabels) !== JSON.stringify(nextLabels)) {
      createAndUpdateLabelMutate({
        card_id: cardId,
        data: { labels: nextLabels },
      });

      setSelectedLabels(nextLabels);
      setShowLabel(false);
      setPrevLabels(nextLabels);
    } else {
      setShowLabel(false);
    }

    if (trimmedInput) {
      setSelectedLabels(nextLabels);
      setInputValue("");
    }
  }, [
    cardId,
    createAndUpdateLabelMutate,
    inputValue,
    prevLabels,
    selectedLabels,
    setShowLabel,
  ]);

  const handleInputChange = React.useCallback((event, newInputValue) => {
    setSelectedLabels([...new Set(newInputValue)]);
  }, []);

  const handleKeyDown = React.useCallback(
    (event) => {
      if (event.key === "Enter" && !isError) {
        event.preventDefault();
        submitLabels();
      }
    },
    [isError, submitLabels]
  );

  const handleBlur = React.useCallback(() => {
    if (!isError) {
      submitLabels();
    }

    setTimeout(() => {
      setShowLabel(false);
    }, 300);
  }, [isError, setShowLabel, submitLabels]);

  const handleTextChange = React.useCallback((event, newInputValue) => {
    if (newInputValue?.length > 30) {
      setIsError(true);
      return;
    }

    setIsError(false);
    setInputValue(newInputValue);
  }, []);

  return {
    inputValue,
    selectedLabels,
    options,
    isError,
    handleInputChange,
    handleKeyDown,
    handleBlur,
    handleTextChange,
  };
}
